import { useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Calendar as CalendarIcon, Plane, Hotel, MapPin, Users } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";

function DateField({ value, onChange, label, testid, minDate }) {
  return (
    <div>
      <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <Popover>
        <PopoverTrigger asChild>
          <button
            data-testid={testid}
            className="w-full h-12 inline-flex items-center justify-between px-3 rounded-lg border border-slate-200 bg-white hover:border-brand transition-colors text-sm"
            type="button"
          >
            <span className={value ? "text-slate-800" : "text-slate-400"}>
              {value ? format(value, "EEE, dd MMM") : "Select date"}
            </span>
            <CalendarIcon size={16} className="text-slate-500" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-auto" align="start">
          <Calendar
            mode="single"
            selected={value}
            onSelect={onChange}
            disabled={(d) => (minDate ? d < minDate : false)}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default function SearchWidget() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("flights");
  // Flight
  const [fFrom, setFFrom] = useState("Delhi (DEL)");
  const [fTo, setFTo] = useState("Dubai (DXB)");
  const [fDepart, setFDepart] = useState(new Date());
  const [fReturn, setFReturn] = useState();
  const [fPax, setFPax] = useState("1 Adult, Economy");
  // Hotel
  const [hCity, setHCity] = useState("Dubai");
  const [hIn, setHIn] = useState(new Date());
  const [hOut, setHOut] = useState();
  const [hGuests, setHGuests] = useState("2 Adults, 1 Room");
  // Destination
  const [dQuery, setDQuery] = useState("");

  return (
    <div
      data-testid="search-widget"
      className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-4 md:p-6"
    >
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 p-1 rounded-xl">
          <TabsTrigger value="flights" data-testid="tab-flights" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-brand data-[state=active]:shadow-sm gap-2 px-4 py-2">
            <Plane size={16} /> Flights
          </TabsTrigger>
          <TabsTrigger value="hotels" data-testid="tab-hotels" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-brand data-[state=active]:shadow-sm gap-2 px-4 py-2">
            <Hotel size={16} /> Hotels
          </TabsTrigger>
          <TabsTrigger value="destinations" data-testid="tab-destinations" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-brand data-[state=active]:shadow-sm gap-2 px-4 py-2">
            <MapPin size={16} /> Destinations
          </TabsTrigger>
        </TabsList>

        <TabsContent value="flights" className="mt-5">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 md:gap-4">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1">From</div>
              <input value={fFrom} onChange={(e) => setFFrom(e.target.value)} data-testid="flight-from" className="w-full h-12 px-3 rounded-lg border border-slate-200 focus:outline-none focus:border-brand text-sm" />
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1">To</div>
              <input value={fTo} onChange={(e) => setFTo(e.target.value)} data-testid="flight-to" className="w-full h-12 px-3 rounded-lg border border-slate-200 focus:outline-none focus:border-brand text-sm" />
            </div>
            <DateField value={fDepart} onChange={setFDepart} label="Depart" testid="flight-depart" minDate={new Date(new Date().setHours(0,0,0,0))} />
            <DateField value={fReturn} onChange={setFReturn} label="Return" testid="flight-return" minDate={fDepart || new Date()} />
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1">Travellers</div>
              <input value={fPax} onChange={(e) => setFPax(e.target.value)} data-testid="flight-pax" className="w-full h-12 px-3 rounded-lg border border-slate-200 focus:outline-none focus:border-brand text-sm" />
            </div>
          </div>
          <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-end">
            <button
              data-testid="search-flights-btn"
              onClick={() => navigate(`/flights?from=${encodeURIComponent(fFrom)}&to=${encodeURIComponent(fTo)}`)}
              className="h-12 px-7 rounded-lg btn-accent font-semibold text-sm uppercase tracking-wider"
            >
              Search Flights
            </button>
          </div>
        </TabsContent>

        <TabsContent value="hotels" className="mt-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 md:gap-4">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1">City / Hotel</div>
              <input value={hCity} onChange={(e) => setHCity(e.target.value)} data-testid="hotel-city" className="w-full h-12 px-3 rounded-lg border border-slate-200 focus:outline-none focus:border-brand text-sm" />
            </div>
            <DateField value={hIn} onChange={setHIn} label="Check-in" testid="hotel-checkin" minDate={new Date(new Date().setHours(0,0,0,0))} />
            <DateField value={hOut} onChange={setHOut} label="Check-out" testid="hotel-checkout" minDate={hIn || new Date()} />
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1">Guests & Rooms</div>
              <input value={hGuests} onChange={(e) => setHGuests(e.target.value)} data-testid="hotel-guests" className="w-full h-12 px-3 rounded-lg border border-slate-200 focus:outline-none focus:border-brand text-sm" />
            </div>
          </div>
          <div className="mt-5 flex justify-end">
            <button
              data-testid="search-hotels-btn"
              onClick={() => navigate(`/hotels?city=${encodeURIComponent(hCity)}`)}
              className="h-12 px-7 rounded-lg btn-accent font-semibold text-sm uppercase tracking-wider"
            >
              Search Hotels
            </button>
          </div>
        </TabsContent>

        <TabsContent value="destinations" className="mt-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 md:gap-4">
            <div className="md:col-span-3">
              <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1">Where do you want to go?</div>
              <input value={dQuery} onChange={(e) => setDQuery(e.target.value)} placeholder="e.g. Bali, Dubai, Maldives…" data-testid="dest-query" className="w-full h-12 px-3 rounded-lg border border-slate-200 focus:outline-none focus:border-brand text-sm" />
            </div>
            <button
              data-testid="search-destinations-btn"
              onClick={() => navigate(`/destinations${dQuery ? `?q=${encodeURIComponent(dQuery)}` : ""}`)}
              className="h-12 mt-6 px-7 rounded-lg btn-accent font-semibold text-sm uppercase tracking-wider"
            >
              Explore
            </button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
